import React, { useState, useEffect } from "react";
import {
    Card,
    CardBody,
    CardHeader,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    Row,
    Col,
    Label,
    Input,
} from "reactstrap";
import DataTable from "react-data-table-component";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPlus,
    faSpinner,
    faSave,
} from "@fortawesome/free-solid-svg-icons";
import { useForm, Controller } from "react-hook-form";
import BreadCrumbs from "../../../components/BreadCrumbs";
import ButtonComponent from "../../../components/ButtonComponent";
import {
    useSaveHolidayMutation,
    useDeleteHolidayMutation,
    Holiday,
} from "../../../redux/services/holidayService";
import { useFirebaseHolidayList } from "../../../hooks/useFirebaseMenu";
import Swal from "sweetalert2";
import EditButtonComponent from "../../../components/EditButtonComponent";
import DeleteButtonComponent from "../../../components/DeleteButtonComponent";
import TableLoadingComponent from "../../../components/TableLoadingComponent";

interface HolidayFormData {
    reason: string;
    date: string;
    shopOpenTime: string;
    shopCloseTime: string;
    isFullDay: boolean;
}

const Holidays: React.FC = () => {
    const [showModal, setShowModal] = useState(false);
    const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [filterType, setFilterType] = useState<"all" | "past" | "current">("current");

    const selectedRestaurantId = useSelector(
        (state: any) => state.restaurant.selectedRestaurantDetails.id
    );

    // API hooks
    const { data: holidaysResponse, loading: isLoading } = useFirebaseHolidayList(selectedRestaurantId);

    const [saveHoliday] = useSaveHolidayMutation();
    const [deleteHoliday] = useDeleteHolidayMutation();

    // Filter holidays based on selected filter
    const filteredHolidays = React.useMemo(() => {
        const holidaysData = holidaysResponse?.holidays || [];
        if (!holidaysData.length) return [];
        
        const now = new Date();
        now.setHours(0, 0, 0, 0); // Set to start of today
        const todayTimestamp = now.getTime() / 1000; // Convert to seconds
        
        if (filterType === "all") {
            return holidaysData;
        } else if (filterType === "past") {
            return holidaysData.filter(holiday => holiday.date.seconds < todayTimestamp);
        } else { // current
            return holidaysData.filter(holiday => holiday.date.seconds >= todayTimestamp);
        }
    }, [holidaysResponse, filterType]);

    // Form handling
    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm<HolidayFormData>({
        defaultValues: {
            reason: "",
            date: "",
            shopOpenTime: "",
            shopCloseTime: "",
            isFullDay: false,
        },
    });

    const isFullDay = watch("isFullDay");

    useEffect(() => {
        if (editingHoliday) {
            // Convert Firestore timestamp to Date object
            const dateObj = new Date(editingHoliday.date.seconds * 1000);
            
            // Extract date components using local time (backend stores in local timezone)
            const year = dateObj.getFullYear();
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const day = String(dateObj.getDate()).padStart(2, '0');
            const dateStr = `${year}-${month}-${day}`;

            const openTimeStr = editingHoliday.openTime
                ? new Date(editingHoliday.openTime.seconds * 1000).toTimeString().slice(0, 5)
                : "";

            const closeTimeStr = editingHoliday.closeTime
                ? new Date(editingHoliday.closeTime.seconds * 1000).toTimeString().slice(0, 5)
                : "";

            reset({
                reason: editingHoliday.reason,
                date: dateStr,
                shopOpenTime: openTimeStr,
                shopCloseTime: closeTimeStr,
                isFullDay: editingHoliday.isFullDay,
            });
        } else {
            reset({
                reason: "",
                date: "",
                shopOpenTime: "",
                shopCloseTime: "",
                isFullDay: false,
            });
        }
    }, [editingHoliday, reset]);

    const handleAddNew = () => {
        setEditingHoliday(null);
        setShowModal(true);
    };

    const handleEdit = React.useCallback((holiday: Holiday) => {
        setEditingHoliday(holiday);
        setShowModal(true);
    }, []);

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingHoliday(null);
        reset();
    };

    const onSubmit = async (data: HolidayFormData) => {
        if (!selectedRestaurantId) {
            toast.error("Please select a restaurant");
            return;
        }

        setIsSubmitting(true);

        try {
            // Convert date and times to ISO strings without timezone conversion
            const dateOnly = `${data.date}T00:00:00.000Z`;
            const shopOpenTime = data.isFullDay
                ? undefined
                : `${data.date}T${data.shopOpenTime}:00.000Z`;
            const shopCloseTime = data.isFullDay
                ? undefined
                : `${data.date}T${data.shopCloseTime}:00.000Z`;

            const requestBody = {
                restaurantId: selectedRestaurantId,
                ...(editingHoliday?.id && { holidayId: editingHoliday.id }),
                reason: data.reason,
                dateTime: dateOnly,
                ...(shopOpenTime && { shopOpenTime }),
                ...(shopCloseTime && { shopCloseTime }),
                isFullDay: data.isFullDay,
            };

            await saveHoliday(requestBody).unwrap();

            toast.success(
                editingHoliday
                    ? "Holiday updated successfully"
                    : "Holiday added successfully"
            );
            handleCloseModal();
            // Firebase will automatically update the data via real-time listener
        } catch (error: any) {
            console.error("Error saving holiday:", error);
            toast.error(
                error?.data?.message ||
                `Failed to ${editingHoliday ? "update" : "add"} holiday`
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = React.useCallback(async (holiday: Holiday) => {
        if (!holiday.id) return;

        const result = await Swal.fire({
            title: "Are you sure?",
            text: `Do you want to delete the holiday "${holiday.reason}"?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "Cancel",
        });

        if (result.isConfirmed) {
            // Show loading state
            Swal.fire({
                title: "Deleting...",
                text: "Please wait while we delete the holiday",
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => {
                    Swal.showLoading();
                },
            });

            try {
                await deleteHoliday({
                    restaurantId: selectedRestaurantId,
                    holidayId: holiday.id,
                }).unwrap();

                Swal.close();
                toast.success("Holiday deleted successfully");
                // Firebase will automatically update the data via real-time listener
            } catch (error: any) {
                console.error("Error deleting holiday:", error);
                Swal.close();
                toast.error(error?.data?.message || "Failed to delete holiday");
            }
        }
    }, [selectedRestaurantId, deleteHoliday]);

    const formatDateTime = (timestamp?: { seconds: number; nanoseconds: number }) => {
        if (!timestamp) return "-";
        const date = new Date(timestamp.seconds * 1000);
        return date.toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const formatDate = (timestamp: { seconds: number; nanoseconds: number }) => {
        if (!timestamp) return "-";
        const date = new Date(timestamp.seconds * 1000);
        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    const columns = React.useMemo(() => [
        {
            name: "Reason",
            selector: (row: Holiday) => row.reason,
            sortable: true,
            minWidth: "200px",
        },
        {
            name: "Date",
            selector: (row: Holiday) => row.date.seconds,
            sortable: true,
            format: (row: Holiday) => formatDate(row.date),
            minWidth: "180px",
        },
        {
            name: "Full Day",
            selector: (row: Holiday) => row.isFullDay,
            sortable: true,
            format: (row: Holiday) => (
                <span className={`badge ${row.isFullDay ? "bg-danger" : "bg-success"}`}>
                    {row.isFullDay ? "Yes" : "No"}
                </span>
            ),
            center: true,
            minWidth: "100px",
        },
        {
            name: "Open Time",
            selector: (row: Holiday) => row.openTime?.seconds || 0,
            sortable: true,
            format: (row: Holiday) =>
                row.isFullDay ? "-" : formatDateTime(row.openTime),
            minWidth: "180px",
        },
        {
            name: "Close Time",
            selector: (row: Holiday) => row.closeTime?.seconds || 0,
            sortable: true,
            format: (row: Holiday) =>
                row.isFullDay ? "-" : formatDateTime(row.closeTime),
            minWidth: "180px",
        },
        {
            name: "Actions",
            cell: (row: Holiday) => (
                <div className="d-flex gap-2">
                    <EditButtonComponent title="Edit Holiday"
                        onClick={() => handleEdit(row)} />
                    <DeleteButtonComponent title="Delete Holiday"
                        onClick={() => handleDelete(row)} />
                </div>
            ),
            center: true,
            minWidth: "120px",
        },
    ], [handleEdit, handleDelete]);
    return (
        <>
            <BreadCrumbs hasSubMenus={true} />
            <Card className="mt-3">
                {isLoading ? (<TableLoadingComponent numColumns={5} />) : (
                    <>
                        <CardHeader className="header-bg-color d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Holidays Management</h5>
                            <div className="d-flex gap-2 align-items-center">
                                <Input
                                    type="select"
                                    value={filterType}
                                    onChange={(e) => setFilterType(e.target.value as "all" | "past" | "current")}
                                    style={{ width: "200px" }}
                                >
                                    <option value="current">Current Holidays</option>
                                    <option value="past">Past Holidays</option>
                                    <option value="all">All</option>
                                </Input>
                                <ButtonComponent onClick={handleAddNew} titel="Add Holiday" icon={<FontAwesomeIcon icon={faPlus} />} />
                            </div>
                        </CardHeader>
                        <CardBody>
                            <DataTable
                                columns={columns}
                                data={filteredHolidays}
                                pagination
                                paginationPerPage={10}
                                paginationRowsPerPageOptions={[10, 20, 30, 50]}
                                highlightOnHover
                                responsive
                                striped
                                noDataComponent={
                                    <div className="text-center py-5">
                                        <p className="text-muted">No holidays found</p>
                                    </div>
                                }
                            />
                        </CardBody>
                    </>
                )}
            </Card>


            {/* Add/Edit Modal */}
            <Modal isOpen={showModal} toggle={handleCloseModal} size="lg">
                <ModalHeader toggle={handleCloseModal}>
                    {editingHoliday ? "Edit Holiday" : "Add New Holiday"}
                </ModalHeader>
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <ModalBody>
                        <Row>
                            <Col md={12} className="mb-3">
                                <Label for="reason">
                                    Reason <span className="text-danger">*</span>
                                </Label>
                                <Controller
                                    name="reason"
                                    control={control}
                                    rules={{
                                        required: "Reason is required",
                                        minLength: {
                                            value: 3,
                                            message: "Reason must be at least 3 characters",
                                        },
                                    }}
                                    render={({ field }) => (
                                        <Input
                                            {...field}
                                            type="text"
                                            id="reason"
                                            placeholder="e.g., Christmas, New Year"
                                            invalid={!!errors.reason}
                                        />
                                    )}
                                />
                                {errors.reason && (
                                    <span className="text-danger small">
                                        {errors.reason.message}
                                    </span>
                                )}
                            </Col>

                            <Col md={6} className="mb-3">
                                <Label for="date">
                                    Date <span className="text-danger">*</span>
                                </Label>
                                <Controller
                                    name="date"
                                    control={control}
                                    rules={{ required: "Date is required" }}
                                    render={({ field }) => (
                                        <Input
                                            {...field}
                                            type="date"
                                            id="date"
                                            invalid={!!errors.date}
                                        />
                                    )}
                                />
                                {errors.date && (
                                    <span className="text-danger small">{errors.date.message}</span>
                                )}
                            </Col>

                            <Col md={6} className="mb-3">
                                <Label for="isFullDay" className="d-block">
                                    &nbsp;
                                </Label>
                                <div className="form-check form-switch">
                                    <Controller
                                        name="isFullDay"
                                        control={control}
                                        render={({ field: { value, onChange, ...field } }) => (
                                            <Input
                                                {...field}
                                                type="checkbox"
                                                id="isFullDay"
                                                checked={value}
                                                onChange={(e) => onChange(e.target.checked)}
                                                className="form-check-input"
                                                style={{ cursor: "pointer" }}
                                            />
                                        )}
                                    />
                                    <Label className="form-check-label" for="isFullDay">
                                        Full Day Holiday
                                    </Label>
                                </div>
                            </Col>

                            {!isFullDay && (
                                <>
                                    <Col md={6} className="mb-3">
                                        <Label for="shopOpenTime">
                                            Open Time <span className="text-danger">*</span>
                                        </Label>
                                        <Controller
                                            name="shopOpenTime"
                                            control={control}
                                            rules={{
                                                required: !isFullDay && "Open time is required",
                                            }}
                                            render={({ field }) => (
                                                <Input
                                                    {...field}
                                                    type="time"
                                                    id="shopOpenTime"
                                                    invalid={!!errors.shopOpenTime}
                                                />
                                            )}
                                        />
                                        {errors.shopOpenTime && (
                                            <span className="text-danger small">
                                                {errors.shopOpenTime.message}
                                            </span>
                                        )}
                                    </Col>

                                    <Col md={6} className="mb-3">
                                        <Label for="shopCloseTime">
                                            Close Time <span className="text-danger">*</span>
                                        </Label>
                                        <Controller
                                            name="shopCloseTime"
                                            control={control}
                                            rules={{
                                                required: !isFullDay && "Close time is required",
                                            }}
                                            render={({ field }) => (
                                                <Input
                                                    {...field}
                                                    type="time"
                                                    id="shopCloseTime"
                                                    invalid={!!errors.shopCloseTime}
                                                />
                                            )}
                                        />
                                        {errors.shopCloseTime && (
                                            <span className="text-danger small">
                                                {errors.shopCloseTime.message}
                                            </span>
                                        )}
                                    </Col>
                                </>
                            )}
                        </Row>

                        <div className="alert alert-info mt-3">
                            <small>
                                <strong>Note:</strong> If "Full Day Holiday" is enabled, the
                                restaurant will be closed for the entire day. Otherwise, specify
                                the open and close times for partial closure.
                            </small>
                        </div>
                    </ModalBody>

                    <ModalFooter>
                        <Button
                            color="secondary"
                            onClick={handleCloseModal}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <ButtonComponent
                            type="submit"
                            isDisabled={isSubmitting}
                            titel={
                                isSubmitting
                                    ? "Saving..."
                                    : editingHoliday
                                        ? "Update Holiday"
                                        : "Add Holiday"
                            }
                            icon={
                                <FontAwesomeIcon
                                    icon={isSubmitting ? faSpinner : faSave}
                                    spin={isSubmitting}
                                />
                            }
                        />
                    </ModalFooter>
                </Form>
            </Modal>
        </>
    );
};

export default Holidays;